import React, { useState, useEffect } from "react";
import { InputAdornment } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArcBox,
  ArcStack,
  ArcText,
  ArcIconButton,
  ArcOutlinedInput,
  ArcPagination,
} from "@src/core/ui";
import { OrdersSkeleton } from "../../../components";
import { ArcBaseSidebar, ArcOrderCard } from "@src/core/components";
import { setMapLocation } from "@src/core/controllers";
import { arcHistoryDestinationPoint, arcHistoryTakeoffPoint } from "@src/core/extensions";
import { PAGINATION_DEFAULT_SIZE } from "@src/core/config";
import {
  OrderStatusEnum,
  OrderEntity,
  OrderMissionEntity,
} from "@src/features/order/entities";
import { orderFeatureApi, useGetOrdersByStatusQuery } from "@src/features/order/redux/api";
import { isNewOrder, isMissionOrder } from "@src/features/order/utils";
import {
  setSelectedHistoryOrder,
  selectSelectedHistoryOrder,
} from "@src/features/history/redux";
import { useGetHubsQuery } from "@src/features/hubs/redux";
import { useGetUserDataQuery } from "@src/core/features";
import { useDisclosure } from "@src/core/hooks";
import { ReactComponent as SearchIcon } from "@src/assets/icons/search.svg";
import { ReactComponent as CloseIcon } from "@src/assets/icons/close.svg";
import { ReactComponent as FilterIcon } from "@src/assets/icons/filter.svg";
import FilterPanel from "./filter";
import FilterChip from "./filter/filter-chip";
import {
  countAppliedFilters,
  getDateFilterLabel,
  getOrderTypeLabel,
  resolveDateRange,
} from "./filter/date-range";
import {
  areOrderFiltersEqual,
  parseOrderFilters,
  writeOrderFilters,
} from "./filter/url-state";
import { DatePreset, OrderFilters } from "./filter/types";

const HISTORY_LIST_PAGE_SIZE = PAGINATION_DEFAULT_SIZE;

const SEARCH_DEBOUNCE_MS = 350;

const PAGE_FRESHNESS_S = 60;

interface AppliedChip {
  key: string;
  label: string;
  onRemove: () => void;
}

const normalizeOrderStatus = (raw: string | null | undefined): OrderStatusEnum | null => {
  if (!raw) return null;
  const status = raw.toLowerCase() as OrderStatusEnum;
  return Object.values(OrderStatusEnum).includes(status) ? status : null;
};

export const HistoryOrdersSidebar: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const selectedOrder = useSelector(selectSelectedHistoryOrder);
  const [searchParams, setSearchParams] = useSearchParams();
  const filterPanel = useDisclosure();
  const { data: profile } = useGetUserDataQuery();


  const canFilterByHub = !profile?.roleStatus?.isOperator;
  const { data: hubs = [] } = useGetHubsQuery(undefined, { skip: !canFilterByHub });

  const readFilters = React.useCallback(
    (params: URLSearchParams): OrderFilters => {
      const parsed = parseOrderFilters(params);
      return canFilterByHub ? parsed : { ...parsed, hubIds: [] };
    },
    [canFilterByHub],
  );

  const [appliedFilters, setAppliedFilters] = useState<OrderFilters>(() =>
    readFilters(searchParams),
  );

  const [page, setPage] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchPage, setSearchPage] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [searchQuery]);

  const resetListState = React.useCallback(() => {
    dispatch(setSelectedHistoryOrder(null));
    setPage(0);
    setSearchPage(0);
  }, [dispatch]);

  useEffect(() => {
    const fromUrl = readFilters(searchParams);
    if (areOrderFiltersEqual(appliedFilters, fromUrl)) return;
    resetListState();
    setAppliedFilters(fromUrl);
  }, [searchParams, appliedFilters, resetListState, readFilters]);

  const isSearchMode = Boolean(debouncedSearch);
  const listPage = isSearchMode ? searchPage : page;

  const { dateFrom, dateTo } = resolveDateRange(appliedFilters);
  const appliedCount = countAppliedFilters(appliedFilters);

  // Sorted so that functionally identical filter sets share one RTK Query cache key.
  const statusParam = React.useMemo(
    () => appliedFilters.orderTypes.map((status) => status.toUpperCase()).sort(),
    [appliedFilters.orderTypes]
  );
  const hubIdParam = React.useMemo(
    () => [...appliedFilters.hubIds].sort((a, b) => a - b),
    [appliedFilters.hubIds]
  );

  const queryArgs = React.useMemo(
    () => ({
      ...(statusParam.length ? { status: statusParam } : {}),
      page: listPage,
      size: HISTORY_LIST_PAGE_SIZE,
      ...(isSearchMode ? { search: debouncedSearch } : {}),
      ...(hubIdParam.length ? { hubId: hubIdParam } : {}),
      ...(dateFrom ? { dateFrom } : {}),
      ...(dateTo ? { dateTo } : {}),
    }),
    [statusParam, hubIdParam, listPage, isSearchMode, debouncedSearch, dateFrom, dateTo]
  );

  const { data: ordersData, isLoading, isFetching, isError } = useGetOrdersByStatusQuery(
    queryArgs,
    { refetchOnMountOrArgChange: PAGE_FRESHNESS_S }
  );

  const prefetchOrders = orderFeatureApi.usePrefetch("getOrdersByStatus");
  const fallbackStatus =
    appliedFilters.orderTypes.length === 1 ? appliedFilters.orderTypes[0] : undefined;

  useEffect(() => {
    return () => {
      dispatch(setSelectedHistoryOrder(null));
    };
  }, [dispatch]);

  const onSelectOrder = (
    order: OrderEntity | OrderMissionEntity,
    status?: OrderStatusEnum
  ) => {
    const withStatus = status ? { ...order, status } : order;

    if (isNewOrder(order)) {
      dispatch(
        setMapLocation({
          latitude: Number(order.deliveryLocation.latitude),
          longitude: Number(order.deliveryLocation.longitude),
        })
      );
      dispatch(setSelectedHistoryOrder(withStatus));
    }

    if (isMissionOrder(order)) {
      // Centre on the mission's own route, not on where its drone happens to be now.
      const center = arcHistoryDestinationPoint(order) ?? arcHistoryTakeoffPoint(order);

      dispatch(
        setMapLocation(
          center
            ? { latitude: center.lat, longitude: center.lng }
            : {
              latitude: Number(order.drone?.latitude),
              longitude: Number(order.drone?.longitude),
            }
        )
      );
      dispatch(setSelectedHistoryOrder(withStatus));
    }
  };

  const onApplyFilters = (filters: OrderFilters) => {
    resetListState();
    setAppliedFilters(filters);
    setSearchParams(writeOrderFilters(searchParams, filters), { replace: true });
    filterPanel.hide();
  };

  useEffect(() => {
    setSearchPage(0);
  }, [debouncedSearch]);

  const displayOrders: OrderEntity[] = ordersData?.content || [];
  const totalCount = ordersData?.totalElements ?? 0;
  const totalPages = ordersData?.totalPages ?? 0;
  const currentPage = listPage;
  const showPagination = totalCount > 0 && totalPages > 1;
  const isLoadingList = isLoading || isFetching;

  const handlePageChange = React.useCallback(
    (_: unknown, newPage: number) => {
      if (isSearchMode) {
        setSearchPage(newPage - 1);
      } else {
        setPage(newPage - 1);
      }
    },
    [isSearchMode]
  );

  // Warm the next page while the operator reads the current one, so paging forward
  // costs no visible round trip.
  useEffect(() => {
    if (isLoadingList || currentPage + 1 >= totalPages) return;
    prefetchOrders({ ...queryArgs, page: currentPage + 1 }, { ifOlderThan: PAGE_FRESHNESS_S });
  }, [isLoadingList, currentPage, totalPages, queryArgs, prefetchOrders]);

  useEffect(() => {
    if (isLoadingList || displayOrders.length === 0 || selectedOrder != null) return;
    onSelectOrder(displayOrders[0], normalizeOrderStatus(displayOrders[0].status) ?? fallbackStatus);
  }, [isLoadingList, displayOrders, selectedOrder]);

  const hubName = (hubId: number): string =>
    hubs.find((hub) => hub.id === hubId)?.name ?? t("history.filter.hub.unknown", { id: hubId });

  const appliedChips: AppliedChip[] = [
    ...appliedFilters.orderTypes.map((status) => ({
      key: `status-${status}`,
      label: getOrderTypeLabel(status, t),
      onRemove: () =>
        onApplyFilters({
          ...appliedFilters,
          orderTypes: appliedFilters.orderTypes.filter((value) => value !== status),
        }),
    })),
    ...appliedFilters.hubIds.map((hubId) => ({
      key: `hub-${hubId}`,
      label: hubName(hubId),
      onRemove: () =>
        onApplyFilters({
          ...appliedFilters,
          hubIds: appliedFilters.hubIds.filter((value) => value !== hubId),
        }),
    })),
  ];

  if (appliedFilters.datePreset !== DatePreset.ALL_TIME) {
    appliedChips.push({
      key: "date",
      label: getDateFilterLabel(appliedFilters, t),
      onRemove: () =>
        onApplyFilters({
          ...appliedFilters,
          datePreset: DatePreset.ALL_TIME,
          customStart: null,
          customEnd: null,
        }),
    });
  }

  if (filterPanel.open) {
    return (
      <ArcBaseSidebar>
        <ArcStack marginTop={0.5} paddingBottom={1.7} sx={{ height: "100%", minHeight: 0 }}>
          <FilterPanel
            initialFilters={appliedFilters}
            canFilterByHub={canFilterByHub}
            onApply={onApplyFilters}
            onClose={filterPanel.hide}
          />
        </ArcStack>
      </ArcBaseSidebar>
    );
  }

  return (
    <ArcBaseSidebar>
      <ArcStack spacing={2} marginTop={0.5} paddingBottom={1.7} sx={{ height: "100%", minHeight: 0 }}>
        <ArcStack spacing={1} direction="row" alignItems="center">
          <ArcOutlinedInput
            placeholder={t("history.orders.searchPlaceholder")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            fullWidth
            autoComplete="off"
            sx={{
              flex: 1,
              "& .MuiFormHelperText-root": { display: "none" },
              "& .MuiInputBase-root": { height: 40 },
              "& .MuiInputBase-input": { color: "white" },
              "& .MuiInputBase-input::placeholder": {
                color: "text.secondary",
                opacity: 1,
              },
            }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start" sx={{ color: "text.secondary" }}>
                    <SearchIcon style={{ width: 16, height: 16 }} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery ? (
                  <InputAdornment position="end" sx={{ color: "text.secondary" }}>
                    <CloseIcon
                      style={{ width: 16, height: 16, cursor: "pointer" }}
                      onClick={() => setSearchQuery("")}
                      role="button"
                      aria-label={t("history.orders.clearSearch")}
                    />
                  </InputAdornment>
                ) : null,
              },
            }}
          />

          <ArcIconButton
            onClick={filterPanel.show}
            aria-label={t("history.filter.button")}
            sx={{
              flexShrink: 0,
              color: "common.white",
              "& path": { fill: "currentColor" },
            }}
          >
            <ArcStack width={24} minWidth={24} position="relative">
              <FilterIcon width={24} height={24} aria-hidden />
              {appliedCount > 0 && (
                <ArcBox
                  position="absolute"
                  top={-6}
                  right={-8}
                  width={16}
                  height={16}
                  borderRadius="50%"
                  bgcolor="brand.main"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <ArcText.caption
                    fontSize={10}
                    fontWeight={500}
                    lineHeight={1}
                    letterSpacing={0}
                    color="text.night"
                  >
                    {appliedCount}
                  </ArcText.caption>
                </ArcBox>
              )}
            </ArcStack>
          </ArcIconButton>
        </ArcStack>

        {appliedChips.length > 0 && (
          <ArcStack direction="row" flexWrap="wrap" alignItems="center" spacing={1} sx={{ rowGap: 1 }}>
            {appliedChips.map((chip) => (
              <FilterChip key={chip.key} label={chip.label} onRemove={chip.onRemove} />
            ))}
          </ArcStack>
        )}

        <ArcStack spacing={1} sx={{ flex: 1, minHeight: 0 }}>
          <ArcStack
            spacing={1}
            sx={{
              flex: 1,
              overflowY: "auto",
              minHeight: 0,
            }}
          >
            {!isLoadingList && displayOrders.map((order, index) => {
              const isSelected = selectedOrder
                ? (isMissionOrder(selectedOrder) && isMissionOrder(order) && selectedOrder.missionId === order.missionId) ||
                  (isNewOrder(selectedOrder) && isNewOrder(order) && selectedOrder.id === order.id)
                : false;

              const cardStatus = normalizeOrderStatus(order.status) ?? fallbackStatus;

              return (
                <ArcOrderCard
                  key={order.missionId || order.id || index}
                  order={order}
                  isSelected={isSelected}
                  onClick={() => onSelectOrder(order, cardStatus)}
                  index={index}
                  status={cardStatus}
                  fullWidth={true}
                />
              );
            })}

            {isError && !isLoadingList && (
              <ArcStack alignItems="center" py={4}>
                <ArcText.h6 color="text.secondary" textAlign="center">
                  {appliedFilters.hubIds.length
                    ? t("history.orders.loadFailedHub")
                    : t("history.orders.loadFailed")}
                </ArcText.h6>
              </ArcStack>
            )}

            {!isError && displayOrders.length === 0 && !isLoadingList && (
              <ArcStack alignItems="center" py={4}>
                <ArcText.h6 color="text.secondary">
                  {t("history.orders.empty")}
                </ArcText.h6>
              </ArcStack>
            )}

            {isLoadingList && <OrdersSkeleton count={HISTORY_LIST_PAGE_SIZE} />}
          </ArcStack>

          {showPagination && (
            <ArcStack alignItems="center" mt={2}>
              <ArcPagination
                page={currentPage + 1}
                count={totalPages}
                disabled={isLoadingList}
                onChange={handlePageChange}
              />
            </ArcStack>
          )}
        </ArcStack>
      </ArcStack>
    </ArcBaseSidebar>
  );
};
